<template>
  <div v-if="!loaded" class="uk-container uk-position-center">
    Just a moment...
  </div>
  <div v-else class="uk-container uk-margin-medium-top">
    <div v-if="pricingLoaded" uk-grid>
      <div class="uk-width-1-3@m uk-width-1-4@l uk-width-1-1">
        <div
          class="uk-card uk-card-default uk-card-body uk-width-1-1 uk-card-small uk-margin-small-bottom"
        >
          <h1 class="uk-h3 uk-margin-remove-top">{{ projectData.name }}</h1>
          <button
            class="uk-button uk-button-default uk-width-1-1 uk-margin-small"
            uk-toggle="target:#cart"
          >
            <span v-if="projectData.type !== 'Distributor'">{{
              settings.viewCartLabel
            }}</span
            ><span v-if="projectData.type == 'Distributor'">{{
              settings.orderSummaryLabel
            }}</span>
            <span class="uk-badge uk-margin-small-left">{{
              totalQuantity
            }}</span>
          </button>
          <div v-if="!showProductChangeForm">
            <button
              v-if="!revisionSubmitted"
              class="uk-button uk-button-default uk-width-1-1 uk-margin-small"
              @click="toggleProductChangeForm()"
            >
              {{ settings.requestChangesLabel }}
            </button>
            <button
              v-if="revisionSubmitted"
              disabled
              class="uk-button uk-button-default uk-width-1-1 uk-margin-small"
            >
              {{ settings.requestSubmittedText }}
            </button>
          </div>
          <div v-if="showProductChangeForm">
            <div class="uk-margin uk-margin-medium-top">
              <textarea
                :placeholder="settings.productChangeFormPlaceholder"
                :aria-label="settings.productChangeFormPlaceholder"
                v-model="revisionRequest"
                class="uk-textarea uk-margin-small uk-margin-remove-bottom"
                rows="3"
              ></textarea>
            </div>
            <image-upload
              v-if="!revisionAttachment"
              class="uk-margin-small"
              destination="revisions"
              @file-uploaded="revisionAttachmentUploaded"
            />
            <p
              v-if="revisionAttachment"
              class="uk-align-center uk-margin-small"
            >
              {{ settings.attachmentUploadedText }}
            </p>
            <div
              uk-grid
              class="uk-child-width-1-2 uk-grid-small uk-margin-medium-bottom"
            >
              <div>
                <button
                  class="uk-button uk-button-default uk-width-1-1"
                  v-on:click="toggleProductChangeForm()"
                >
                  {{ settings.cancelButtonText }}
                </button>
              </div>
              <div>
                <button
                  class="uk-button uk-button-primary uk-width-1-1"
                  @click="submitRevision()"
                >
                  {{ settings.submitButtonText }}
                </button>
              </div>
            </div>
          </div>
          <router-link
            v-if="
              projectData.type == 'Distributor' ||
                projectData.type == 'Organization'
            "
            :to="'/project/' + projectData.projectID + '/add-patterns'"
          >
            <button class="uk-button uk-button-default uk-width-1-1">
              {{ settings.addNewDesignsText }}
            </button>
          </router-link>
          <hr class="uk-margin-remove-bottom" />
          <p :projectLink="projectLink" class="uk-margin-small">
            {{ settings.shareThisProjectText }}
          </p>
          <div class="uk-inline uk-width-1-1" @click="copyProjectLink">
            <button
              type="button"
              class="uk-form-icon uk-form-icon-flip"
              uk-icon="icon: copy"
              aria-label="Copy project link"
            ></button>
            <input
              v-model="projectLink"
              :aria-label="settings.shareThisProjectText"
              class="uk-input uk-margin-small"
              readonly
            />
          </div>
          <div v-if="projectData.type == 'Distributor'" class="uk-margin-small">
            <hr class="uk-margin-remove-bottom" />
            <p
              v-html="
                formatContent(settings.distributorOrderSummaryMessage.json)
              "
            />
          </div>
        </div>
      </div>
      <div class="uk-width-1-1 uk-width-2-3@m uk-width-3-4@l">
        <div
          class="uk-child-width-1-1 uk-child-width-1-3@l uk-child-width-1-2@m uk-grid uk-grid-match"
          uk-grid
        >
          <div v-for="card in customCardData" :key="card.index">
            <div class="uk-card uk-card-default uk-card-body uk-card-small">
              <h4 class="uk-margin-small">{{ card.heading }}</h4>
              <p
                :style="{ color: card.textColor && card.textColor.value }"
                class="uk-margin-small uk-text-break"
                v-html="formatContent(card.content.json)"
              />
            </div>
          </div>
          <div
            v-for="(product, index) in filteredProducts"
            :key="`product${index}`"
            class="product-card"
          >
            <product-view
              v-if="loaded"
              :product="product"
              :pricing-info="pricingInfo"
              :project="projectData"
              :standard-pricing="standardPricing"
              :standard-project="standardProject"
              :ref="'_' + product.productId"
              :data="
                productDetails.filter(
                  p => p.productType == product.productData.productType
                )[0]
              "
              :settings="settings"
              :shipping-content="formatShippingContent(settings.shippingTable)"
              @qty-change="setVisibleProductQuantity"
              @add-to-cart="addToCart"
              @order-swatch="orderSwatch"
            />
          </div>
        </div>
      </div>
    </div>

    <cart
      :project="projectData"
      :pricing-info="pricingInfo"
      :data="productDetails"
      :discount="discount"
      :discount-code="discountCode"
      :discount-error="discountError"
      :settings="settings"
      :discountSettings="discountSettings"
      @remove-cart-item="removeCartItem"
      @remove-swatch="removeSwatch"
      @clear-cart="clearCart"
      @add-discount="addDiscount"
      @select-packaging="selectPackaging"
    />
  </div>
</template>

<script>
import Cart from "@/components/design-portal/Cart.vue";
import ProductView from "@/components/design-portal/ProductView.vue";
import firebase from "firebase";
import CrmService from "@/services/CrmService";
import ProjectsService from "@/services/ProjectsService";
import ImageUpload from "@/components/image-upload/ImageUpload.vue";

export default {
  name: "ProjectView",
  data() {
    return {
      pricingLoaded: false,
      loaded: false,
      customCardData: [],
      productDetails: [],
      showProductChangeForm: false,
      revisionSubmitted: false,
      revisionRequest: "",
      revisionAttachment: "",
      projectData: {},
      firebaseID: "",
      products: [],
      standardPricing: {},
      linkCopied: false,
      settings: {},
      discountSettings: {},
      visibleProductQuantity: {},
      discount: {},
      discountError: false,
      discountCode: null,
      selectedPackagingCost: 0
    };
  },
  methods: {
    copyProjectLink() {
      // Ignore clicks while the "Link copied!" feedback is showing so we
      // never copy the feedback text itself.
      if (this.linkCopied) return;
      const link = this.projectLink;
      const fallbackCopy = () =>
        this.$copyText(link).then(
          () => this.toggleLinkCopied(),
          () => alert("Could not copy the link. Please copy it manually.")
        );
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard
          .writeText(link)
          .then(() => this.toggleLinkCopied(), fallbackCopy);
      } else {
        fallbackCopy();
      }
    },
    // New catch-all standard tier
    getStandardPricing(pi, option, combined, options) {
      for (var i = this.standardPricing[pi].length - 1; i >= 0; i--) {
        let tier = this.standardPricing[pi][i];
        var combinedQuantities = 0;
        if (combined && options) {
          let cleanedOptions = [...new Set(options)];
          for (const op of cleanedOptions) {
            combinedQuantities += this.quantities[pi + op.replaceAll('"', "")];
          }
          if (
            combinedQuantities >= tier.min &&
            combinedQuantities <= tier.max
          ) {
            if (pi == "tableRunner") {
              return {
                min: tier.min,
                max: tier.max,
                price: tier.price[option]
              };
            } else {
              return {
                min: tier.min,
                max: tier.max,
                price: tier.price[_.camelCase(option)]
              };
            }
          }
        } else if ((!combined || combined == null) && options) {
          if (
            this.quantities[pi + option] >= tier.min &&
            this.quantities[pi + option] <= tier.max
          ) {
            return {
              min: tier.min,
              max: tier.max,
              price: tier.price[_.camelCase(option)]
            };
          }
        } else {
          if (
            this.quantities[pi] >= tier.min &&
            this.quantities[pi] <= tier.max
          ) {
            return tier;
          }
        }
      }
    },
    // New catch-all standard base price tier
    getStandardBasePricing(pi, option, combined, options) {
      const tier = this.standardPricing[pi][0];
      if (options) {
        if (pi == "tableRunner") {
          return { price: tier.price[option] };
        } else {
          return { price: tier.price[_.camelCase(option)] };
        }
      } else {
        return { price: tier.price };
      }
    },
    // New catch-all standard tiers
    getStandardPricingTiers(pi) {
      return this.standardPricing[pi];
    },
    toggleLinkCopied() {
      this.linkCopied = true;
      setTimeout(() => {
        this.linkCopied = false;
      }, 2000);
    },
    toggleProductChangeForm() {
      if (this.showProductChangeForm) {
        this.showProductChangeForm = false;
        this.revisionAttachment = "";
        this.revisionRequest = "";
      } else {
        this.showProductChangeForm = true;
      }
    },
    setVisibleProductQuantity(arr) {
      const currentProduct = this.productDetails.filter(
        p => p.productType == arr[1]
      );
      const options = arr[2] || null;
      if (currentProduct[0].customPricing) {
        const variantMapping = currentProduct[0].sampleProductData.variants.map(
          v => {
            return { name: v.title, firebaseId: v.sku };
          }
        );
        var v = "";
        if (options) {
          for (const value of Object.values(options)) {
            v += value;
            if (
              Object.values(options).indexOf(value) !==
              Object.values(options).length - 1
            ) {
              v += " / ";
            }
          }
        }
        const variant = variantMapping.filter(vv => vv.name === v);
        if (variant[0] && variant[0].firebaseId) {
          const variantName =
            variant[0].firebaseId.charAt(0).toUpperCase() +
            variant[0].firebaseId.slice(1);
          this.visibleProductQuantity[
            `${_.camelCase(arr[1])}${variantName.replaceAll('"', "")}`
          ] = arr[0];
        } else {
          this.visibleProductQuantity[_.camelCase(arr[1])] = arr[0];
        }
      } else {
        this.visibleProductQuantity[_.camelCase(arr[1])] = arr[0];
      }
    },
    setQuantity(arr) {
      if (arr[3]) {
        const variantName = arr[3].charAt(0).toUpperCase() + arr[3].slice(1);
        this.pricingInfo.counts[`${arr[1]}${variantName.replaceAll('"', "")}`] =
          arr[0];
      } else {
        this.pricingInfo.counts[arr[1]] = arr[0];
      }
      this.pricingInfo.unitPrice = this.unitPrice;
    },
    async addToCart(newItem) {
      const type = newItem.productData.productType;
      const currentProduct = this.productDetails.filter(
        p => p.productType == type
      );
      var baseItem;
      var v = "";
      var option = "";
      const productOptions = currentProduct[0].sampleProductData.options;
      if (currentProduct[0].customPricing) {
        const options = currentProduct[0].variantMapping;
        for (const option of productOptions) {
          v += newItem.variantSelections[_.camelCase(option.name)];
          if (productOptions.indexOf(option) !== productOptions.length - 1) {
            v += " / ";
          }
        }
        const arr = options && options.filter(vv => vv.name == v);
        option = arr.length && arr[0].firebaseId;
        option = option.charAt(0).toUpperCase() + option.slice(1);
        baseItem = _.camelCase(type) + option.replaceAll('"', "");
      } else {
        baseItem = _.camelCase(type);
      }

      let quantityUpdated = false;
      for (let index = 0; index < this.projectData.cart.length; index++) {
        let cartItem = this.projectData.cart[index];
        if (
          _.isEqual(cartItem.variantSelections, newItem.variantSelections) &&
          cartItem.productData.proofID == newItem.productData.proofID
        ) {
          this.projectData.cart[index].quantity += parseInt(newItem.quantity);
          quantityUpdated = true;
        }
      }
      if (!quantityUpdated) {
        let lineItem = {
          product: {
            title:
              newItem.productData.proofID +
              " / " +
              newItem.productData.productType,
            options: ["Title"],
            variants: [
              {
                options: [],
                price: this.standardProject
                  ? this.pricingInfo.basePrice[baseItem]
                  : 0,
                weight: 199,
                weightUnit: "GRAMS",
                requiresShipping:
                  newItem.productData.productType != "Custom Tag"
              }
            ],
            tags:
              "configured product, Recommendations disabled, Hidden recommendation",
            productType:
              this.projectData.type + " - " + newItem.productData.productType,
            metafields: [
              {
                namespace: "seo",
                key: "hidden",
                value: "1",
                type: "integer"
              }
            ],
            category: {
              id: newItem.productCategory
            }
          },
          media: [
            {
              originalSource:
                "http://storage.googleapis.com/knotty-custom.appspot.com/proof-images/" +
                newItem.fileName,
              mediaContentType: "IMAGE"
            }
          ]
        };
        for (
          let index = 0;
          index < Object.keys(newItem.variantSelections).length;
          index++
        ) {
          const variantKey = Object.keys(newItem.variantSelections)[index];
          if (newItem.variantSelections[variantKey]) {
            lineItem.product.title +=
              " / " + newItem.variantSelections[variantKey];
          }
        }
        let shopifyProduct = await ProjectsService.createCustomLineItem(
          lineItem
        );
        newItem.shopifyProductID = shopifyProduct.data.product.productVariantsBulkCreate.product.id
          .split("/")
          .pop();
        newItem.shopifyVariantID =
          shopifyProduct.data.product.productVariantsBulkCreate.productVariants[0].id;
        this.projectData.cart.push(newItem);
      }
      quantityUpdated = false;
    },
    orderSwatch(item) {
      this.projectData.swatches.push(item);
    },
    revisionAttachmentUploaded(url) {
      this.revisionAttachment = url;
    },
    removeCartItem(item) {
      this.projectData.cart.splice(item, 1);
    },
    removeSwatch(item) {
      this.projectData.swatches.splice(item, 1);
    },
    async addDiscount(discount) {
      const checkout = await ProjectsService.addDiscount({
        discount
      });
      if (checkout.data.codeDiscountNodeByCode === null) {
        this.discountError = true;
      } else {
        if (
          checkout.data.codeDiscountNodeByCode.codeDiscount.status !== "ACTIVE"
        ) {
          this.discountError = true;
        } else {
          this.discountError = false;
          this.discountCode = discount;
          this.discount = checkout.data.codeDiscountNodeByCode;
        }
      }
    },
    updateFirebaseCart() {
      var projectRef = firebase
        .firestore()
        .collection("projects")
        .doc(this.firebaseID);
      var self = this;
      return projectRef
        .update({
          cart: this.projectData.cart
        })
        .then(function() {})
        .catch(function(error) {
          // The document probably doesn't exist.
          console.error("Error updating document: ", error);
        });
    },
    updateFirebaseSwatches() {
      var projectRef = firebase
        .firestore()
        .collection("projects")
        .doc(this.firebaseID);
      var self = this;
      // Set the "capital" field of the city 'DC'
      return projectRef
        .update({
          swatches: this.projectData.swatches
        })
        .then(function() {})
        .catch(function(error) {
          // The document probably doesn't exist.
          console.error("Error updating document: ", error);
        });
    },
    submitRevision() {
      var self = this;
      let revision = {
        dealID: self.projectData.projectID,
        proofID: "",
        note: self.revisionRequest,
        attachment: self.revisionAttachment
      };
      CrmService.revisionRequest(revision);
      self.toggleProductChangeForm();
      this.revisionSubmitted = true;
      setTimeout(function() {
        self.revisionSubmitted = false;
      }, 2000);
    },
    selectPackaging(totalPackageCost) {
      this.selectedPackagingCost = totalPackageCost;
    },
    clearCart() {
      this.projectData.cart = [];
      this.projectData.swatches = [];
    },
    getContentfulData: async () => {
      const query = `{
        portalSettingsCollection(limit: 1) {
          items {
            viewCartLabel
            orderSummaryLabel
            requestChangesLabel
            productChangeFormPlaceholder
            requestSubmittedText
            allowDigitalSwatchFulfillment
            orderSwatchLabel
            swatchAndProductNoticeFirstLine
            swatchAndProductNoticeSecondLine
            individualSwatchCost
            swatchShippingLabel
            swatchShippedCost
            swatchDigitalLabel
            swatchDigitalCost
            swatchInCartNotice
            swatchEmailedProductLabel
            noSwatchSelectedError
            attachmentUploadedText
            submitButtonText
            cancelButtonText
            addNewDesignsText
            shareThisProjectText
            viewProductLabel
            designerNotesLabel
            distributorOrderSummaryMessage {
              json
            }
            shippingMessage
            shippingTable {
              json
            }
            customTagShippingMessage
            alertsCollection {
              items {
                alertName
                keyword
                alertDescription
              }
            }
            swatchMessage
            addToCartRegular
            addToCartDistributor
            minimumText
            bulkPriceRegular
            bulkPriceDistributor
            bulkQuantityInstructions
            enablePackagingOptions
            packageSelectionSectionTitle
            minimalPackagingTitle
            minimalPackagingSubtitle
            minimalPackagingDescription
            minimalPackagingClarification
            minimalPackagingCost
            individualPackagingTitle
            individualPackagingDescription
            individualPackagingCost
            individualPackagingClarification
            packagingSubtotalLabel
            noPackagingSelectedError
          }
        }
        portalDiscountsCollection {
          items {
            discountCartLabel
            discountCartLabelColor
            discountSubheadingCartColor
            noTierReachedCartLabel
            tiersNotReachedCartLabel
            topTierReachedCartLabel
            discountAmountCartColor
            enableDiscounts
            discountType
            tierOneThreshold
            tierOneLabel
            tierOneDiscountAmount
            tierTwoThreshold
            tierTwoLabel
            tierTwoDiscountAmount
            tierThreeThreshold
            tierThreeLabel
            tierThreeDiscountAmount
            tierFourThreshold
            tierFourLabel
            tierFourDiscountAmount
            percentageDiscountLabel
            percentageDiscountAmount

          }
        }
        portalCardsCollection {
          total
          items {
            sys {
              id
            }
            cardCollection {
              total
              items {
                heading
                content {
                  json
                }
                textColor
              }
            }
          }
        }
      }`;
      const fetchUrl = `https://graphql.contentful.com/content/v1/spaces/${process.env.CONTENTFUL_SPACE_ID}`;
      const fetchOptions = {
        method: "POST",
        headers: {
          Authorization: `Bearer ${process.env.CONTENTFUL_ACCESS_TOKEN}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          query
        })
      };
      try {
        const response = await fetch(fetchUrl, fetchOptions).then(response =>
          response.json()
        );
        return response.data;
      } catch (error) {
        console.log(error);
        throw new Error("Could not receive the data from Contentful!");
      }
    },
    getContentfulProductData: async () => {
      const query = `{
        portalProductCollection {
          items {
            productType
            productDescription {
              json
            }
            pricingInformation {
              json
            }
            pricingIdentifier
            sampleProduct
            customPricing
            bundleCounting
            minimumQuantity
            friendlyOptionNames
          }
        }
      }`;
      const fetchUrl = `https://graphql.contentful.com/content/v1/spaces/${process.env.CONTENTFUL_SPACE_ID}`;
      const fetchOptions = {
        method: "POST",
        headers: {
          Authorization: `Bearer ${process.env.CONTENTFUL_ACCESS_TOKEN}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          query
        })
      };
      try {
        const response = await fetch(fetchUrl, fetchOptions).then(response =>
          response.json()
        );
        return response.data;
      } catch (error) {
        console.log(error);
        throw new Error("Could not receive the data from Contentful!");
      }
    },
    formatContent(content) {
      console.log(content);
      var newContent = document.createElement("div");
      for (const block of content.content[0].content) {
        if (block.nodeType == "text") {
          let text = document.createElement("span");
          text.innerText = block.value;
          newContent.appendChild(text);
        }
        if (block.nodeType == "hyperlink") {
          let link = document.createElement("a");
          link.href = block.data.uri;
          link.innerText = block.content[0].value;
          newContent.appendChild(link);
        }
      }
      return newContent.innerHTML;
    },
    async fetchOptions(product) {
      if (product && product.sampleProduct) {
        const shopifyData = await ProjectsService.getProductById({
          id: product.sampleProduct
        });
        return shopifyData.data;
      }
      return "";
    },
    formatShippingContent(content) {
      var newContent = document.createElement("div");
      for (const block of content.json.content) {
        if (block.nodeType == "paragraph") {
          let text = document.createElement("p");
          for (content in block.content) {
            text.innerHTML += block.content[content].value.replaceAll(
              "\n",
              "<br />"
            );
          }

          newContent.appendChild(text);
        }
        if (block.nodeType == "text") {
          let text = document.createElement("span");
          text.innerText = block.value;
          newContent.appendChild(text);
        }
        if (block.nodeType == "hyperlink") {
          let link = document.createElement("a");
          link.href = block.data.uri;
          link.innerText = block.content[0].value;
          newContent.appendChild(link);
        }
        if (block.nodeType == "table") {
          let table = document.createElement("table");
          table.classList.add("uk-table", "uk-table-divider");
          for (const tableContent of block.content) {
            if (tableContent.nodeType == "table-row") {
              let row = document.createElement("tr");
              for (const rowItem of tableContent.content) {
                if (rowItem.nodeType == "table-header-cell") {
                  let th = document.createElement("th");
                  th.innerText = rowItem.content[0].content[0].value;
                  row.appendChild(th);
                } else if (rowItem.nodeType == "table-cell") {
                  let td = document.createElement("td");
                  td.innerText = rowItem.content[0].content[0].value;
                  row.appendChild(td);
                }
              }
              table.appendChild(row);
            }
          }
          newContent.appendChild(table);
        }
      }
      return btoa(newContent.innerHTML);
    }
  },
  watch: {
    firebaseID: function() {
      var self = this;
      firebase
        .firestore()
        .collection("projects")
        .doc(this.firebaseID)
        .collection("products")
        .orderBy("created", "desc")
        .get()
        .then(function(querySnapshot) {
          querySnapshot.forEach(function(doc) {
            let product = {
              productId: doc.id,
              productData: doc.data()
            };
            self.products.push(product);
          });
        });
    },
    cart: {
      deep: true,
      handler(newValue, oldValue) {
        if (oldValue) {
          this.updateFirebaseCart();
        }
      }
    },
    swatches: {
      deep: true,
      handler(newValue, oldValue) {
        if (oldValue) {
          this.updateFirebaseSwatches();
        }
      }
    }
  },
  computed: {
    standardProject() {
      return this.projectData.type !== "Distributor";
    },
    filteredProducts() {
      return this.products.filter(
        product =>
          !product.productData.hidden &&
          this.productDetails.filter(
            p => p.productType == product.productData.productType
          )[0].pricingIdentifier
      );
    },
    quantities() {
      let counts = {};
      // 1. Fetch all types of counts and add to "counts" object
      let uniqueProductTypes = [
        ...new Map(
          this.products.map(p => [p.productData.productType, p])
        ).values()
      ];
      let productTypes = uniqueProductTypes.map(p => p.productData.productType);
      for (const product of productTypes) {
        const currentProduct = this.productDetails.filter(
          p => p.productType == product
        );
        if (currentProduct[0].customPricing) {
          const variantMapping = currentProduct[0].sampleProductData.variants.map(
            v => {
              return { name: v.title, firebaseId: v.sku };
            }
          );
          this.productDetails.filter(
            p => p.productType == product
          )[0].variantMapping = variantMapping;

          for (const variant of variantMapping) {
            if (variant.firebaseId) {
              const variantName =
                variant.firebaseId.charAt(0).toUpperCase() +
                variant.firebaseId.slice(1);
              counts[
                `${_.camelCase(product)}${variantName.replaceAll('"', "")}`
              ] = 0;
            } else {
              counts[_.camelCase(product)] = 0;
            }
          }
        } else {
          counts[_.camelCase(product)] = 0;
        }
      }
      // 2. Make sure counts include what is already in cart
      if (this.projectData.cart && this.projectData.cart.length) {
        for (const cartItem of this.projectData.cart) {
          const type = cartItem.productData.productType;
          const currentProduct = this.productDetails.filter(
            p => p.productType == type
          );
          var v = "";
          var option = "";
          const productOptions = currentProduct[0].sampleProductData.options;
          if (currentProduct[0].customPricing) {
            const options = currentProduct[0].variantMapping;
            for (const option of productOptions) {
              v += cartItem.variantSelections[_.camelCase(option.name)];
              if (
                productOptions.indexOf(option) !==
                productOptions.length - 1
              ) {
                v += " / ";
              }
            }
            const arr = options && options.filter(vv => vv.name == v);
            option = arr.length && arr[0].firebaseId;
            option = option.charAt(0).toUpperCase() + option.slice(1);
            counts[
              `${_.camelCase(
                cartItem.productData.productType
              )}${option.replaceAll('"', "")}`
            ] += cartItem.quantity;
          } else {
            counts[_.camelCase(cartItem.productData.productType)] +=
              cartItem.quantity;
          }
        }
      }

      // Pull in visible product quantities (allegedly)
      for (const [key, value] of Object.entries(counts)) {
        if (this.visibleProductQuantity[key]) {
          counts[key] += this.visibleProductQuantity[key];
        }
      }

      return counts;
    },
    totalQuantity() {
      let count =
        (this.projectData.swatches && this.projectData.swatches.length) || 0;
      for (
        let index = 0;
        index < (this.projectData.cart && this.projectData.cart.length);
        index++
      ) {
        const element = this.projectData.cart[index];
        count += element.quantity;
      }
      return count;
    },
    pricingModel() {
      if (this.projectData.type == "Distributor") {
        return "distributor";
      } else {
        return "standard";
      }
    },
    unitPrice() {
      if (this.pricingModel == "standard") {
        let uniqueProductTypes = [
          ...new Map(
            this.products.map(p => [p.productData.productType, p])
          ).values()
        ];
        let productTypes = uniqueProductTypes.map(
          p => p.productData.productType
        );
        let pricing = {};

        for (const product of productTypes) {
          var price = null;
          const currentProduct = this.productDetails.filter(
            p => p.productType == product
          );
          const productItem = this.products.filter(
            p => p.productData.productType === currentProduct[0]
          );
          if (productItem.length) {
            this.products.filter(
              p => p.productData.productType === currentProduct[0]
            )[0].productData.pricingIdentifier =
              currentProduct[0].pricingIdentifier;
          }
          if (currentProduct[0].pricingIdentifier !== null) {
            if (currentProduct[0].customPricing) {
              const variantMapping = currentProduct[0].sampleProductData.variants.map(
                v => {
                  return { name: v.title, firebaseId: v.sku };
                }
              );
              const variantNames = variantMapping.map(
                variant =>
                  variant.firebaseId.charAt(0).toUpperCase() +
                  variant.firebaseId.slice(1)
              );
              for (const variant of variantMapping) {
                if (variant.firebaseId) {
                  const variantName =
                    variant.firebaseId.charAt(0).toUpperCase() +
                    variant.firebaseId.slice(1);
                  price = this.getStandardPricing(
                    currentProduct[0].pricingIdentifier,
                    variantName,
                    currentProduct[0].bundleCounting,
                    variantNames
                  );
                  pricing[
                    `${_.camelCase(product)}${variantName.replaceAll('"', "")}`
                  ] = price.price;
                }
              }
            } else {
              price = this.getStandardPricing(
                currentProduct[0].pricingIdentifier
              );
              pricing[_.camelCase(product)] = price.price;
            }
          }
        }
        return pricing;
      }
    },
    basePrice() {
      if (this.pricingModel == "standard") {
        let uniqueProductTypes = [
          ...new Map(
            this.products.map(p => [p.productData.productType, p])
          ).values()
        ];
        let productTypes = uniqueProductTypes.map(
          p => p.productData.productType
        );
        let pricing = {};

        for (const product of productTypes) {
          var price = null;
          const currentProduct = this.productDetails.filter(
            p => p.productType == product
          );
          const productItem = this.products.filter(
            p => p.productData.productType === currentProduct[0]
          );
          if (productItem.length) {
            this.products.filter(
              p => p.productData.productType === currentProduct[0]
            )[0].productData.pricingIdentifier =
              currentProduct[0].pricingIdentifier;
          }
          if (currentProduct[0].pricingIdentifier !== null) {
            if (currentProduct[0].customPricing) {
              const variantMapping = currentProduct[0].sampleProductData.variants.map(
                v => {
                  return { name: v.title, firebaseId: v.sku };
                }
              );
              const variantNames = variantMapping.map(
                variant =>
                  variant.firebaseId.charAt(0).toUpperCase() +
                  variant.firebaseId.slice(1)
              );
              for (const variant of variantMapping) {
                if (variant.firebaseId) {
                  const variantName =
                    variant.firebaseId.charAt(0).toUpperCase() +
                    variant.firebaseId.slice(1);
                  price = this.getStandardBasePricing(
                    currentProduct[0].pricingIdentifier,
                    variantName,
                    currentProduct[0].bundleCounting,
                    variantNames
                  );
                  pricing[
                    `${_.camelCase(product)}${variantName.replaceAll('"', "")}`
                  ] = price.price;
                }
              }
            } else {
              price = this.getStandardBasePricing(
                currentProduct[0].pricingIdentifier
              );
              pricing[_.camelCase(product)] = price.price;
            }
          }
          if (price) {
            if (currentProduct[0].customPricing) {
              pricing[`${_.camelCase(product)}${_.camelCase()}`] = price.price;
            }
          }
        }
        return pricing;
      }
    },
    productSubTotal() {
      var total = 0;
      if (this.projectData.type == "Distributor") {
        return total;
      } else {
        for (const cartItem of this.cart) {
          var option = "";
          var v = "";
          const type = cartItem.productData.productType;
          const currentProduct = this.productDetails.filter(
            p => p.productType == type
          );
          const productOptions = currentProduct[0].sampleProductData.options;
          if (currentProduct[0].customPricing) {
            const options = currentProduct[0].variantMapping;
            for (const option of productOptions) {
              v += cartItem.variantSelections[_.camelCase(option.name)];
              if (
                productOptions.indexOf(option) !==
                productOptions.length - 1
              ) {
                v += " / ";
              }
            }
            const arr = options && options.filter(vv => vv.name == v);
            option = arr.length && arr[0].firebaseId;
            option = option.charAt(0).toUpperCase() + option.slice(1);
            total +=
              cartItem.quantity *
              this.unitPrice[
                `${_.camelCase(type)}${option.replaceAll('"', "")}`
              ];
          } else {
            total += cartItem.quantity * this.unitPrice[_.camelCase(type)];
          }
        }
        for (let i = 0; i < this.cart.length; i++) {
          const lineItem = this.cart[i];
          if (
            (lineItem.variantSelections &&
              lineItem.variantSelections.style &&
              lineItem.variantSelections.style.includes("filter")) ||
            (lineItem.variantSelections &&
              lineItem.variantSelections.style &&
              lineItem.variantSelections.style.includes("moisture")) ||
            (lineItem.variantSelections &&
              lineItem.variantSelections.style &&
              lineItem.variantSelections.style.includes("Filter"))
          ) {
            if (this.projectData.type != "Distributor") {
              total += lineItem.quantity * 3;
            } else {
              total += lineItem.quantity * 2.1;
            }
          } else if (
            lineItem.variantSelections &&
            lineItem.variantSelections.layers
          ) {
            if (
              lineItem.variantSelections &&
              lineItem.variantSelections.layers.includes("Double")
            ) {
              if (this.projectData.type != "Distributor") {
                total += lineItem.quantity * 4;
              } else {
                total += lineItem.quantity * 2.8;
              }
            }
          }
        }
        return total;
      }
    },
    subTotal() {
      var total = Number(this.productSubTotal);
      this.selectedPackagingCost;
      let totalPackagingCost = this.selectedPackagingCost;
      if (
        this.projectData.priceAdjustment.amount &&
        this.projectData.cart.length > 0
      ) {
        total += Number(this.projectData.priceAdjustment.amount);
      }
      if (
        this.projectData.setupCharge > 0 &&
        this.projectData.cart.length > 0
      ) {
        total += Number(this.projectData.setupCharge);
      }
      if (
        this.projectData.customShipping.amount > 0 &&
        this.projectData.cart.length > 0
      ) {
        total += Number(this.projectData.customShipping.amount);
      }
      if (this.projectData.swatches.length > 0) {
        const swatchCost = this.settings.swatchShippedCost;
        total += Number(this.projectData.swatches.length * swatchCost);
      }
      if (totalPackagingCost > 0) {
        total += Number(totalPackagingCost);
        total += Number(this.projectData.swatches.length * 3);
      }

      return total;
    },
    maskUpgradePrice() {
      var upgradePrice = 3;
      if (this.projectData.type == "Distributor") {
        upgradePrice = 2.1;
      }
      return upgradePrice;
    },
    neckGaiterUpgradePrice() {
      var upgradePrice = 4;
      if (this.projectData.type == "Distributor") {
        upgradePrice = 2.8;
      }
      return upgradePrice;
    },
    pricingInfo() {
      return {
        counts: this.quantities,
        basePrice: this.basePrice,
        unitPrice: this.unitPrice,
        pricingModel: this.pricingModel,
        productSubTotal: this.productSubTotal,
        subTotal: this.subTotal
      };
    },
    cart() {
      return this.projectData.cart;
    },
    swatches() {
      return this.projectData.swatches;
    },
    projectLink() {
      const link = "custom.knottytie.com/project/" + this.projectData.projectID;
      if (this.linkCopied) {
        return "Link copied!";
      }
      return link;
    }
  },
  async created() {
    var self = this;

    //get project from firestore by project id
    await firebase
      .firestore()
      .collection("projects")
      .where("projectID", "==", self.$route.params.id)
      .get()
      .then(function(querySnapshot) {
        querySnapshot.forEach(function(doc) {
          self.projectData = Object.assign({}, self.projectData, doc.data());
          self.firebaseID = doc.id;
          self.$emit("project", self.projectData);
          if (!self.projectData.customShipping) {
            self.projectData.customShipping = Object.assign({
              label: "",
              amount: 0
            });
          }
          for (let index = 0; index < self.projectData.cart.length; index++) {
            const item = self.projectData.cart[index];
            if (
              item.productData.productType == "Neck Gaiter" &&
              !item.variantSelections.layers
            ) {
              self.clearCart();
            }
          }
          doc.ref
            .collection("products")
            .get()
            .then(function(querySnapshot) {
              querySnapshot.forEach(function(document) {});
            });
        });
      });

    //get standard pricing
    await firebase
      .firestore()
      .collection("pricing")
      .doc("standard")
      .get()
      .then(async function(doc) {
        if (doc.exists) {
          self.standardPricing = await doc.data();
        } else {
          console.log("No such document!");
        }
      })
      .catch(function(error) {
        console.log("Error getting document:", error);
      });

    const contentfulData = await this.getContentfulData("cardData");
    this.customCardData =
      contentfulData.portalCardsCollection.items[0].cardCollection.items;
    this.settings = contentfulData.portalSettingsCollection.items[0];
    this.discountSettings = contentfulData.portalDiscountsCollection.items[0];
    const contentfulProductData = await this.getContentfulProductData(
      "cardData"
    );
    this.productDetails = contentfulProductData.portalProductCollection.items;
    const productIds = this.productDetails.map(
      product => product.sampleProduct
    );
    const productData = await ProjectsService.getAllProductsById({
      ids: productIds
    });

    for (const product of this.productDetails) {
      product["sampleProductData"] = productData.data.filter(
        p => product.sampleProduct === btoa(p.admin_graphql_api_id)
      )[0];
      if (product.customPricing) {
        const variantMapping = product.sampleProductData.variants.map(v => {
          return { name: v.title, firebaseId: v.sku };
        });

        for (const variant of variantMapping) {
          if (variant.firebaseId) {
            const variantName =
              variant.firebaseId.charAt(0).toUpperCase() +
              variant.firebaseId.slice(1);
            this.$set(
              this.visibleProductQuantity,
              `${_.camelCase(product.productType)}${variantName.replaceAll(
                '"',
                ""
              )}`,
              0
            );
          } else {
            this.$set(
              this.visibleProductQuantity,
              _.camelCase(product.productType),
              0
            );
          }
        }
      } else {
        this.$set(
          this.visibleProductQuantity,
          _.camelCase(product.productType),
          0
        );
      }
    }
    this.loaded = true;
    this.pricingLoaded = true;
  },
  mounted: async function() {},
  components: {
    cart: Cart,
    "product-view": ProductView,
    "image-upload": ImageUpload
  },
  filters: {
    money: function(value) {
      if (!value) return "";

      return "$" + Number(value).toFixed(2);
    }
  }
};
</script>

<style type="text/css">
.pointer {
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/design-portal/ProjectView.vue